import { Checkbox } from '@liveauctioneers/hammer-ui-core/checkbox';
import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { Trash } from '@liveauctioneers/hammer-ui-core/icons';
import { BodySecondary, Caption } from '@liveauctioneers/hammer-ui-core/text';
import { useCallback } from 'react';

import type { SimilarItemAlert } from '@/redux/modules/similarItemAlerts/similarItemAlerts.types';

import { useSimilarItemAlertRow } from '@/hooks/useSimilarItemAlertRow';
import { useAppDispatch } from '@/redux/hooks';
import { toggleSimilarItemAlertEmail } from '@/redux/modules/similarItemAlerts/similarItemAlerts.actions';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { SimilarItemAlertsTableRow } from './SimilarItemAlertsTableRow';

type Props = {
    alert: SimilarItemAlert;
};

/**
 * One row of the Emails & Notifications "Similar Item Alerts" section. The lot's title and thumbnail
 * come from the shared item-summary slice (hydrated by the section); a lot that could not be
 * hydrated still renders using the title savedsearch-api stored on the row, so a gap in item-api
 * never blanks the row.
 */
export const SimilarItemAlertRow = ({ alert }: Props) => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();

    const { dateAdded, imageUrl, lastAlert, onDelete, title } = useSimilarItemAlertRow(alert);

    const onToggleEmail = useCallback(() => {
        dispatch(toggleSimilarItemAlertEmail({ savedItemId: alert.savedItemId, sendAlert: !alert.sendAlert }));
    }, [alert.savedItemId, alert.sendAlert, dispatch]);

    return (
        <SimilarItemAlertsTableRow
            dateAdded={<BodySecondary>{dateAdded}</BodySecondary>}
            email={
                <Checkbox.Option
                    aria-label={String(
                        formatMessage(['notificationsPage', 'toggleEmailNotifications'], { values: { name: title } })
                    )}
                    checked={alert.sendAlert}
                    className="mb-0"
                    name="similarItemAlertEmailCheckbox"
                    onChange={onToggleEmail}
                />
            }
            item={
                <div className="flex min-w-0 items-center gap-sm">
                    <IconButton
                        aria-label={String(formatMessage(['similarItemAlerts', 'deleteAlertLabel']))}
                        icon={Trash}
                        onClick={onDelete}
                        size="sm"
                    />
                    {/* getItemSummaryFirstImageUrl falls back to the shared placeholder image, so
                        there is always something to render — no empty-square branch needed. */}
                    <img
                        alt={title}
                        className="size-[48px] shrink-0 rounded-xs object-cover"
                        src={imageUrl}
                    />
                    <div className="min-w-0">
                        <BodySecondary className="line-clamp-2">{title}</BodySecondary>
                        <Caption
                            className="tabletMin:hidden"
                            color="secondary"
                        >
                            {formatMessage(['similarItemAlerts', 'lastAlertMobile'], { values: { date: lastAlert } })}
                        </Caption>
                    </div>
                </div>
            }
            lastAlert={<BodySecondary>{lastAlert}</BodySecondary>}
        />
    );
};
